import {z} from 'zod'

import {apiUrl} from 'src/api/fetch'
import {tzDateFromIso} from 'src/api/zod-helpers'
import {employmentClassificationSchema} from 'src/assets/workers/schemas'

export const absenceReasonSchema = z.enum(['sick', 'vacation', 'personal', 'other'])
export type AbsenceReason = z.infer<typeof absenceReasonSchema>

export const absenceReasonOptions: {value: AbsenceReason; label: string}[] = [
    {value: 'sick', label: 'Sick'},
    {value: 'vacation', label: 'Vacation / PTO'},
    {value: 'personal', label: 'Personal'},
    {value: 'other', label: 'Other'},
]

export const outOfServiceReasonSchema = z.enum(['broken', 'in_shop', 'inspection', 'other'])
export type OutOfServiceReason = z.infer<typeof outOfServiceReasonSchema>

export const outOfServiceReasonOptions: {value: OutOfServiceReason; label: string}[] = [
    {value: 'broken', label: 'Broken'},
    {value: 'in_shop', label: 'In the shop'},
    {value: 'inspection', label: 'Inspection'},
    {value: 'other', label: 'Other'},
]

export const driverStateSchema = z.enum([
    'available',
    'on_jobs',
    'out_of_hours',
    ...absenceReasonSchema.options,
])
export type DriverState = z.infer<typeof driverStateSchema>

const driverDayRawSchema = z.object({
    worker_id: z.string(),
    name: z.string(),
    employment_classification: employmentClassificationSchema.nullable(),
    home_dispatch_yard_id: z.string().nullable(),
    state: driverStateSchema,
    label: z.string(),
    detail: z.string(),
    hours_left: z.number().nullable(),
    job_display_ids: z.array(z.string()),
})

export const driverDaySchema = driverDayRawSchema.transform((raw) => ({
    workerId: raw.worker_id,
    name: raw.name,
    employmentClassification: raw.employment_classification,
    homeDispatchYardId: raw.home_dispatch_yard_id,
    state: raw.state,
    label: raw.label,
    detail: raw.detail,
    hoursLeft: raw.hours_left,
    jobDisplayIds: raw.job_display_ids,
}))

export type DriverDayRaw = z.infer<typeof driverDayRawSchema>
export type DriverDay = z.infer<typeof driverDaySchema>

const driversForDayRawSchema = z.object({
    date: z.string(),
    hours_note: z.string().nullable(),
    drivers: z.array(driverDayRawSchema),
})

export const driversForDaySchema = driversForDayRawSchema.transform((raw) => ({
    date: raw.date,
    hoursNote: raw.hours_note,
    drivers: raw.drivers.map((driver) => driverDaySchema.parse(driver)),
}))

export type DriversForDayRaw = z.infer<typeof driversForDayRawSchema>

const workerAbsenceRawSchema = z.object({
    id: z.string(),
    worker_id: z.string(),
    reason: absenceReasonSchema,
    label: z.string(),
    start_date: z.string(),
    end_date: z.string(),
    note: z.string().nullable(),
})

export const workerAbsenceSchema = workerAbsenceRawSchema.transform((raw) => ({
    id: raw.id,
    workerId: raw.worker_id,
    reason: raw.reason,
    label: raw.label,
    startDate: raw.start_date,
    endDate: raw.end_date,
    note: raw.note,
}))

export type WorkerAbsenceRaw = z.infer<typeof workerAbsenceRawSchema>
export type WorkerAbsence = z.infer<typeof workerAbsenceSchema>

export const createAbsenceInputSchema = z.object({
    reason: absenceReasonSchema,
    startDate: z.string(),
    endDate: z.string(),
    note: z
        .string()
        .optional()
        .transform((value) => value || null),
})

export type CreateAbsenceInput = z.input<typeof createAbsenceInputSchema>

export const createAbsenceRequestSchema = createAbsenceInputSchema.transform(
    ({reason, startDate, endDate, note}) => ({
        reason,
        start_date: startDate,
        end_date: endDate,
        note,
    }),
)

export type CreateAbsenceRequestRaw = z.infer<typeof createAbsenceRequestSchema>

const outOfServiceRawSchema = z.object({
    id: z.string(),
    rental_asset_id: z.string(),
    reason: outOfServiceReasonSchema,
    label: z.string(),
    note: z.string().nullable(),
    since: z.string(),
    expected_back: z.string().nullable(),
})

export const outOfServiceSchema = outOfServiceRawSchema.transform((raw) => ({
    id: raw.id,
    rentalAssetId: raw.rental_asset_id,
    reason: raw.reason,
    label: raw.label,
    note: raw.note,
    since: raw.since,
    expectedBack: raw.expected_back,
}))

export type OutOfServiceRaw = z.infer<typeof outOfServiceRawSchema>

export const markOutOfServiceInputSchema = z.object({
    reason: outOfServiceReasonSchema,
    note: z
        .string()
        .optional()
        .transform((value) => value || null),
    expectedBack: z
        .string()
        .optional()
        .transform((value) => value || null),
})

export type MarkOutOfServiceInput = z.input<typeof markOutOfServiceInputSchema>

export const markOutOfServiceRequestSchema = markOutOfServiceInputSchema.transform(
    ({reason, note, expectedBack}) => ({
        reason,
        note,
        expected_back: expectedBack,
    }),
)

export type MarkOutOfServiceRequestRaw = z.infer<typeof markOutOfServiceRequestSchema>

const publicationRawSchema = z.object({
    id: z.string(),
    dispatch_yard_id: z.string(),
    yard_name: z.string(),
    board_date: z.string(),
    version: z.number(),
    changes: z.array(z.string()),
    subject: z.string(),
    sent_to: z.string().nullable(),
    email_captured_only: z.boolean(),
    email_failed: z.boolean(),
    published_by: z.string(),
    published_at: z.string(),
    repeated: z.boolean(),
})

export const publicationSchema = publicationRawSchema.transform((raw) => ({
    id: raw.id,
    dispatchYardId: raw.dispatch_yard_id,
    yardName: raw.yard_name,
    boardDate: raw.board_date,
    version: raw.version,
    changes: raw.changes,
    subject: raw.subject,
    sentTo: raw.sent_to,
    emailCapturedOnly: raw.email_captured_only,
    emailFailed: raw.email_failed,
    publishedBy: raw.published_by,
    publishedAt: tzDateFromIso.parse(raw.published_at),
    repeated: raw.repeated,
    emailUrl: apiUrl(`/dispatch/publications/${raw.id}/email`),
}))

export type PublicationRaw = z.infer<typeof publicationRawSchema>
export type Publication = z.infer<typeof publicationSchema>

export const publishScheduleInputSchema = z.object({
    dispatchYardId: z.string(),
    boardDate: z.string(),
    /** From the preview the dispatcher saw; publishing is refused if the board changed since. */
    previewFingerprint: z.string().optional(),
})

export type PublishScheduleInput = z.input<typeof publishScheduleInputSchema>

export const publishScheduleRequestSchema = publishScheduleInputSchema.transform(
    ({dispatchYardId, boardDate, previewFingerprint}) => ({
        dispatch_yard_id: dispatchYardId,
        board_date: boardDate,
        ...(previewFingerprint ? {preview_fingerprint: previewFingerprint} : {}),
    }),
)

const publishPreviewRawSchema = z.object({
    dispatch_yard_id: z.string(),
    yard_name: z.string(),
    board_date: z.string(),
    version: z.number(),
    changes: z.array(z.string()),
    subject: z.string(),
    html: z.string(),
    sent_to: z.string().nullable(),
    unchanged: z.boolean(),
    fingerprint: z.string(),
})

export const publishPreviewSchema = publishPreviewRawSchema.transform((raw) => ({
    dispatchYardId: raw.dispatch_yard_id,
    yardName: raw.yard_name,
    boardDate: raw.board_date,
    version: raw.version,
    changes: raw.changes,
    subject: raw.subject,
    html: raw.html,
    sentTo: raw.sent_to,
    unchanged: raw.unchanged,
    fingerprint: raw.fingerprint,
}))

export type PublishPreviewRaw = z.infer<typeof publishPreviewRawSchema>
export type PublishPreview = z.infer<typeof publishPreviewSchema>

export type PublishScheduleRequestRaw = z.infer<typeof publishScheduleRequestSchema>

const scheduleSummaryRawSchema = z.object({
    id: z.string(),
    board_date: z.string(),
    version: z.number(),
    subject: z.string(),
    sent_to: z.string().nullable(),
    email_captured_only: z.boolean(),
    email_failed: z.boolean(),
    not_published: z.array(z.string()),
    sent_by: z.string(),
    sent_at: z.string(),
    repeated: z.boolean(),
})

export const scheduleSummarySchema = scheduleSummaryRawSchema.transform((raw) => ({
    id: raw.id,
    boardDate: raw.board_date,
    version: raw.version,
    subject: raw.subject,
    sentTo: raw.sent_to,
    emailCapturedOnly: raw.email_captured_only,
    emailFailed: raw.email_failed,
    notPublished: raw.not_published,
    sentBy: raw.sent_by,
    sentAt: tzDateFromIso.parse(raw.sent_at),
    repeated: raw.repeated,
    emailUrl: apiUrl(`/dispatch/summaries/${raw.id}/email`),
}))

export type ScheduleSummaryRaw = z.infer<typeof scheduleSummaryRawSchema>
export type ScheduleSummary = z.infer<typeof scheduleSummarySchema>

export const sendSummaryInputSchema = z.object({boardDate: z.string()})

export type SendSummaryInput = z.input<typeof sendSummaryInputSchema>

export const sendSummaryRequestSchema = sendSummaryInputSchema.transform(({boardDate}) => ({
    board_date: boardDate,
}))

export type SendSummaryRequestRaw = z.infer<typeof sendSummaryRequestSchema>
